import React, { useMemo, useState } from 'react';
import {
  Card,
  Group,
  Text,
  Stack,
  TextInput,
  NumberInput,
  Select,
  SegmentedControl,
  Textarea,
  ActionIcon,
  Tooltip,
  Badge,
  Collapse,
  Box
} from '@mantine/core';
import {
  IconEar,
  IconPhone,
  IconMicrophone,
  IconArrowRight,
  IconPlayerStop,
  IconChevronUp,
  IconChevronDown,
  IconTrash,
  IconChevronRight,
  IconChevronDown as IconExpandDown,
  IconGripVertical
} from '@tabler/icons-react';

/**
 * One mini-form per SIT v1 step. The step type is detected from whichever
 * top-level key is present (expectPrompt / pressDtmf / saySpeech /
 * expectTransfer / hangup). Editing one field re-emits a complete step via
 * onChange so the parent owns the array; the card itself is stateless except
 * for the collapse toggle.
 */

const STEP_TYPES = ['expectPrompt', 'pressDtmf', 'saySpeech', 'expectTransfer', 'hangup'];

const STEP_META = {
  expectPrompt: {
    label: 'Expect prompt',
    icon: IconEar,
    color: 'blue',
    description: 'Wait for the IVR to speak'
  },
  pressDtmf: {
    label: 'Press digits',
    icon: IconPhone,
    color: 'orange',
    description: 'Send touch-tone DTMF digits'
  },
  saySpeech: {
    label: 'Speak',
    icon: IconMicrophone,
    color: 'green',
    description: 'Speak words via TTS to the IVR'
  },
  expectTransfer: {
    label: 'Expect transfer',
    icon: IconArrowRight,
    color: 'violet',
    description: 'Wait for transfer to a queue / agent'
  },
  hangup: {
    label: 'End call',
    icon: IconPlayerStop,
    color: 'gray',
    description: 'Caller hangs up'
  }
};

export function detectStepType(step) {
  if (!step || typeof step !== 'object') return null;
  for (const k of STEP_TYPES) {
    if (step[k]) return k;
  }
  return null;
}

export function summarizeStep(step) {
  const type = detectStepType(step);
  if (!type) return '(unknown step)';
  if (type === 'hangup') return 'Hang up';
  const body = step[type] || {};
  if (type === 'expectPrompt') {
    const m = body.match || {};
    if (m.substring) return `Hear: "${m.substring}"`;
    if (m.regex) return `Hear regex: ${m.regex}`;
    if (m.semantic?.intent) return `Hear (smart): ${m.semantic.intent}`;
    return 'Hear (advanced matcher)';
  }
  if (type === 'pressDtmf') return `Press ${body.digits}`;
  if (type === 'saySpeech') return `Say: "${body.text}"`;
  if (type === 'expectTransfer') {
    const to = body.to || {};
    if (to.queueName) return `Transfer to queue: ${to.queueName}`;
    if (to.agentId) return `Transfer to agent: ${to.agentId}`;
    return 'Transfer';
  }
  return type;
}

/**
 * Detect which "match mode" the matcher object is in. Returns one of:
 *   'substring' — { substring }
 *   'semantic'  — { semantic: { intent, tolerance? } }
 *   'advanced'  — anything else (regex, any/all/not, empty)
 */
function detectMatcherMode(matcher) {
  if (!matcher || typeof matcher !== 'object') return 'substring';
  if (typeof matcher.substring === 'string') return 'substring';
  if (matcher.semantic && typeof matcher.semantic.intent === 'string') return 'semantic';
  return 'advanced';
}

function ExpectPromptForm({ body, onChange }) {
  const matcher = body.match || { substring: '' };
  const mode = detectMatcherMode(matcher);
  const [advancedText, setAdvancedText] = useState(() => JSON.stringify(matcher, null, 2));
  const [advancedError, setAdvancedError] = useState(null);

  const updateMatcher = (nextMatcher) => {
    onChange({ ...body, match: nextMatcher });
  };

  const setMode = (next) => {
    if (next === 'substring') {
      updateMatcher({ substring: matcher.substring || matcher.semantic?.intent || '' });
    } else if (next === 'semantic') {
      updateMatcher({
        semantic: {
          intent: matcher.semantic?.intent || matcher.substring || '',
          tolerance: matcher.semantic?.tolerance || 'medium'
        }
      });
    } else {
      setAdvancedText(JSON.stringify(matcher, null, 2));
    }
  };

  const currentTimeout = body.timeoutSec ?? 5;
  return (
    <Stack gap="sm">
      <Group grow>
        <NumberInput
          label="Timeout (sec)"
          description={
            currentTimeout <= 5
              ? '5s is the touch-tone-menu default. Bump to 10–15s if this step waits on a long IVR prompt or post-DTMF response (the Pause is fixed-duration, not cancellable on match). Max 30s — Chime SDK Pause limit.'
              : 'How long to wait for the IVR to speak (max 30s — Chime SDK Pause limit).'
          }
          value={currentTimeout}
          onChange={(v) => onChange({ ...body, timeoutSec: typeof v === 'number' ? v : 5 })}
          min={1}
          max={30}
        />
        <TextInput
          label="Language"
          description="e.g. en-US, es-ES"
          value={body.language || ''}
          onChange={(e) => {
            const v = e.currentTarget.value;
            const next = { ...body };
            if (v) next.language = v;
            else delete next.language;
            onChange(next);
          }}
        />
      </Group>

      <Text size="sm" fw={500} mt="xs">Match if caller hears…</Text>
      <SegmentedControl
        value={mode}
        onChange={setMode}
        data={[
          { value: 'substring', label: 'Exact phrase' },
          { value: 'semantic', label: 'Smart match' },
          { value: 'advanced', label: 'Advanced' }
        ]}
      />

      {mode === 'substring' && (
        <TextInput
          placeholder='e.g. "thank you for calling"'
          value={matcher.substring || ''}
          onChange={(e) => updateMatcher({ substring: e.currentTarget.value })}
        />
      )}
      {mode === 'semantic' && (
        <Group grow align="flex-start">
          <TextInput
            label="Intent paraphrase"
            description="What the IVR is saying, in your own words"
            placeholder="e.g. company greeting"
            value={matcher.semantic?.intent || ''}
            onChange={(e) =>
              updateMatcher({
                semantic: {
                  intent: e.currentTarget.value,
                  ...(matcher.semantic?.tolerance
                    ? { tolerance: matcher.semantic.tolerance }
                    : {})
                }
              })
            }
          />
          <Select
            label="Tolerance"
            data={[
              { value: 'low', label: 'Low — close match only' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High — loose match' }
            ]}
            value={matcher.semantic?.tolerance || ''}
            onChange={(v) =>
              updateMatcher({
                semantic: {
                  intent: matcher.semantic?.intent || '',
                  ...(v ? { tolerance: v } : {})
                }
              })
            }
            clearable
          />
        </Group>
      )}
      {mode === 'advanced' && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            JSON matcher — supports regex, any/all/not composites. Edit carefully.
          </Text>
          <Textarea
            value={advancedText}
            onChange={(e) => {
              const txt = e.currentTarget.value;
              setAdvancedText(txt);
              try {
                const next = JSON.parse(txt);
                setAdvancedError(null);
                updateMatcher(next);
              } catch (err) {
                setAdvancedError(err.message);
              }
            }}
            autosize
            minRows={3}
            error={advancedError}
            styles={{
              input: { fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }
            }}
          />
        </Stack>
      )}
    </Stack>
  );
}

function PressDtmfForm({ body, onChange }) {
  return (
    <Group grow align="flex-start">
      <TextInput
        label="Digits"
        description="0–9, *, #, A–D"
        value={body.digits || ''}
        onChange={(e) => onChange({ ...body, digits: e.currentTarget.value.toUpperCase() })}
        placeholder="e.g. 1"
      />
      <Select
        label="Signaling"
        data={[
          { value: 'rfc2833', label: 'RFC 2833 (out-of-band)' },
          { value: 'inband', label: 'Inband audio tones' }
        ]}
        value={body.signaling || 'rfc2833'}
        onChange={(v) => onChange({ ...body, signaling: v || 'rfc2833' })}
      />
      <NumberInput
        label="Wait after (ms)"
        description="Post-DTMF settle delay"
        value={body.postDelayMs ?? 500}
        onChange={(v) => onChange({ ...body, postDelayMs: typeof v === 'number' ? v : 500 })}
        min={0}
        max={10000}
        step={100}
      />
    </Group>
  );
}

function SaySpeechForm({ body, onChange }) {
  return (
    <Stack gap="sm">
      <Textarea
        label="Text to speak"
        description="The harness will TTS this to the IVR"
        value={body.text || ''}
        onChange={(e) => onChange({ ...body, text: e.currentTarget.value })}
        autosize
        minRows={2}
      />
      <TextInput
        label="Voice (optional)"
        description="e.g. Joanna, Matthew — leaves default voice when blank"
        value={body.voice || ''}
        onChange={(e) => {
          const v = e.currentTarget.value;
          const next = { ...body };
          if (v) next.voice = v;
          else delete next.voice;
          onChange(next);
        }}
      />
    </Stack>
  );
}

function ExpectTransferForm({ body, onChange }) {
  const to = body.to || {};
  return (
    <Stack gap="sm">
      <Group grow>
        <NumberInput
          label="Timeout (sec)"
          description="Max 30s — Chime SDK Pause limit."
          value={body.timeoutSec ?? 30}
          onChange={(v) => onChange({ ...body, timeoutSec: typeof v === 'number' ? v : 30 })}
          min={1}
          max={30}
        />
      </Group>
      <Group grow>
        <TextInput
          label="Queue name"
          value={to.queueName || ''}
          onChange={(e) => {
            const nextTo = { ...to };
            if (e.currentTarget.value) nextTo.queueName = e.currentTarget.value;
            else delete nextTo.queueName;
            onChange({ ...body, to: nextTo });
          }}
        />
        <TextInput
          label="Agent ID"
          value={to.agentId || ''}
          onChange={(e) => {
            const nextTo = { ...to };
            if (e.currentTarget.value) nextTo.agentId = e.currentTarget.value;
            else delete nextTo.agentId;
            onChange({ ...body, to: nextTo });
          }}
        />
      </Group>
    </Stack>
  );
}

function HangupForm() {
  return (
    <Text size="sm" c="dimmed">
      Caller hangs up. No additional options.
    </Text>
  );
}

const FORMS = {
  expectPrompt: ExpectPromptForm,
  pressDtmf: PressDtmfForm,
  saySpeech: SaySpeechForm,
  expectTransfer: ExpectTransferForm,
  hangup: HangupForm
};

function PlanStepCard({
  step,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  // Drag handle props from a parent <Draggable>. When set, the card renders a
  // grip icon that owns the drag interaction. ↑/↓ buttons stay for keyboard /
  // accessibility fallback.
  dragHandleProps,
  isDragging
}) {
  const [expanded, setExpanded] = useState(false);
  const type = detectStepType(step);
  const meta = type ? STEP_META[type] : null;
  const Icon = meta?.icon;
  const Form = type ? FORMS[type] : null;

  const summary = useMemo(() => summarizeStep(step), [step]);

  const handleBodyChange = (nextBody) => {
    onChange({ ...step, [type]: nextBody });
  };

  return (
    <Card
      withBorder
      padding="sm"
      radius="sm"
      style={isDragging ? { boxShadow: '0 6px 18px rgba(0,0,0,0.15)', background: 'var(--mantine-color-violet-0)' } : undefined}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {dragHandleProps && (
            <Tooltip label="Drag to reorder">
              <Box
                {...dragHandleProps}
                style={{
                  cursor: 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--mantine-color-gray-5)'
                }}
                aria-label="Drag handle"
              >
                <IconGripVertical size={16} />
              </Box>
            </Tooltip>
          )}
          <ActionIcon
            variant="subtle"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <IconExpandDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
          <Badge size="lg" variant="light" color={meta?.color || 'gray'} circle>
            {index + 1}
          </Badge>
          {Icon && (
            <Box
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-gray-1)'
              }}
            >
              <Icon size={18} />
            </Box>
          )}
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={500} size="sm">{meta?.label || 'Step'}</Text>
            <Text size="xs" c="dimmed" lineClamp={1}>{summary}</Text>
          </Stack>
        </Group>
        <Group gap={4} wrap="nowrap">
          <Tooltip label="Move up">
            <ActionIcon variant="subtle" onClick={onMoveUp} disabled={index === 0}>
              <IconChevronUp size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Move down">
            <ActionIcon variant="subtle" onClick={onMoveDown} disabled={index === total - 1}>
              <IconChevronDown size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete step">
            <ActionIcon variant="subtle" color="red" onClick={onDelete}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <Collapse in={expanded}>
        <Box mt="sm" pl="xl">
          <TextInput
            label="Step ID"
            description="Stable identifier (lowercase, dashes)"
            value={step.id || ''}
            onChange={(e) => onChange({ ...step, id: e.currentTarget.value })}
            mb="sm"
          />
          {Form && <Form body={step[type] || {}} onChange={handleBodyChange} />}
          <Select
            label="On failure"
            description="What to do if this step does not pass"
            data={[
              { value: 'continue', label: 'Continue to next step' },
              { value: 'abort', label: 'Abort the call' }
            ]}
            value={step.onFailure || ''}
            onChange={(v) => {
              const next = { ...step };
              if (v) next.onFailure = v;
              else delete next.onFailure;
              onChange(next);
            }}
            mt="sm"
            clearable
          />
        </Box>
      </Collapse>
    </Card>
  );
}

/** Build a fresh step skeleton for a given type. Caller assigns the id.
 *
 *  expectPrompt default is Smart match (semantic, medium tolerance) — real
 *  IVRs reword each turn, and substring matchers force the QA author to
 *  guess the exact phrase. Authors who want a literal substring can switch
 *  the SegmentedControl to "Exact phrase" inline. */
export function makeBlankStep(type, id) {
  switch (type) {
    case 'expectPrompt':
      return {
        id,
        expectPrompt: {
          timeoutSec: 5,
          match: { semantic: { intent: '', tolerance: 'medium' } }
        }
      };
    case 'pressDtmf':
      return { id, pressDtmf: { digits: '', signaling: 'rfc2833', postDelayMs: 500 } };
    case 'saySpeech':
      return { id, saySpeech: { text: '' } };
    case 'expectTransfer':
      return { id, expectTransfer: { timeoutSec: 30 } };
    case 'hangup':
      return { id, hangup: {} };
    default:
      return { id };
  }
}

export default PlanStepCard;
